import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import {
  Tabs as ChakraTabs,
  TabsProps as ChakraTabsProps,
  TabList as ChakraTabList,
  TabProps as ChakraTabProps,
  TabListProps as ChakraTabListProps,
  TabIndicator,
  useTabs,
  useTabsContext,
  TabsProvider,
  useTab,
  useMultiStyleConfig,
  Button,
  Box,
  forwardRef,
  useMergeRefs,
} from '@chakra-ui/react';
import {
  getTabFlexStyles,
  getTabListBorderStyles,
  getTabsContainerStyles,
} from './tabs.theme';
import { TabBadge } from './tabs-badge';
import { TabsShowMoreMenu } from './tabs-showmore-menu';
import { useTabResizeObserver } from './useTabResizeObserver';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';

const MORE_MENU_BUTTON_WIDTH_BOUND = 250;
export const TabsContext = createContext<TabsContextProps>({});

interface TabsContextProps {
  showMoreMenu?: boolean;
  colorMode?: ColorMode;
  setOverflowCount?: React.Dispatch<React.SetStateAction<number>>;
  size?: TabSizes;
  isFitted?: boolean;
}

export type TabSizes = 'lg' | 'md';

export interface TabsProps extends ChakraTabsProps {
  children: React.ReactNode;
  /**
   * The size of the Tabs.
   *  @default md
   * */
  size?: TabSizes;
  /**
   * If `true`, the shows a right badge.
   *  @default horizontal
   * */
  orientation?: 'vertical' | 'horizontal';
  /**
   * If `true`, the More Menu shows and stores overflow tabs.
   *  @default false
   * */
  showMoreMenu?: boolean;
  /** The color mode that the tabs will display.
   * @default light
   */
  colorMode?: ColorMode;
}

export interface TabProps extends ChakraTabProps {
  index?: number;
  /**
   * If `true`, Tab will display a right badge.
   * @default false
   */
  showBadge?: boolean;
  /**
   * The `icon` that will be displayed on the left side.
   * @default null
   */
  icon?: React.ReactElement;
}
export interface TabListProps extends ChakraTabListProps {
  children: React.ReactElement[];
}

/**
 * A React component that helps you build accessible tabs, by providing keyboard interactions and ARIA attributes described
 * in the WAI-ARIA Tab Panel Design Pattern.
 */
export const Tabs = forwardRef<TabsProps, 'div'>(
  (
    {
      children,
      showMoreMenu,
      colorMode: propColorMode,
      size = 'md',
      isFitted = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);
    const tabsProps = useTabs({ ...props });
    const colorScheme = colorMode;

    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      onChange?.(currentIndex);
    }, [currentIndex, onChange]);

    const handleTabChange = (index: number) => {
      setCurrentIndex(index);
    };

    return (
      <TabsProvider value={tabsProps}>
        <TabsContext.Provider
          value={{ showMoreMenu, colorMode, size, isFitted }}
        >
          <ChakraTabs
            ref={ref}
            data-testid="chakra-tabs-custom"
            isManual
            colorScheme={colorScheme}
            size={size}
            onChange={handleTabChange}
            {...props}
          >
            {children}
          </ChakraTabs>
        </TabsContext.Provider>
      </TabsProvider>
    );
  }
);

export const Tab = forwardRef<TabProps, typeof Button>((props, ref) => {
  const { showBadge, icon, index, children, ...otherProps } = props;
  const tabProps = useTab({ ...otherProps, ref });
  const { colorMode, showMoreMenu, size, isFitted } = useContext(TabsContext);
  const { orientation } = useTabsContext();
  const styles = useMultiStyleConfig('Tabs', tabProps);
  return (
    <Button
      colorScheme={colorMode}
      __css={styles.tab}
      sx={getTabFlexStyles(
        colorMode || 'light',
        orientation,
        showMoreMenu || false,
        size || 'md',
        isFitted || false
      )}
      {...tabProps}
    >
      {icon}
      {children}
      {showBadge && <TabBadge badgeIndex={index || 0} colorMode={colorMode} />}
    </Button>
  );
});

export const TabList = forwardRef<TabListProps, 'div'>(
  ({ children, ...props }, ref) => {
    const [tabs, setTabs] = useState<React.ReactNode[]>(
      React.Children.toArray(children)
    );
    const [overflowTabs, setOverflowTabs] = useState<React.ReactNode[]>([]);
    const [tabsListWidth, setTabsListWidth] = useState<number>(0);
    const { showMoreMenu, colorMode, isFitted } = useContext(TabsContext);
    const { orientation, setSelectedIndex, selectedIndex } = useTabsContext();
    const tabListRef = useRef<HTMLDivElement>(null);
    const tabIndicatorRef = useRef<HTMLDivElement>(null);
    const selectedMenuItemRef = useRef<HTMLElement | null>(null);

    const totalTabsWidth = (
      allTabs: React.ReactNode[],
      parentTabWidth: number,
      menuWidthBound: number
    ) =>
      allTabs.reduce(
        (count, _, index) => {
          const childNode = tabListRef?.current?.children[index];
          if (!childNode) return count;

          const childWidth = childNode.getBoundingClientRect().width;
          return count.visibleTabsCount > 0 &&
            count.totalWidth + menuWidthBound > parentTabWidth
            ? count
            : {
                totalWidth: count.totalWidth + childWidth,
                visibleTabsCount: count.visibleTabsCount + 1,
              };
        },
        { totalWidth: 0, visibleTabsCount: 0 }
      );

    const recalculateTabs = useCallback(() => {
      if (!tabListRef.current) return;
      const parentWidth =
        (
          tabListRef.current.parentNode as HTMLDivElement
        )?.getBoundingClientRect().width || 0;
      setTabsListWidth(parentWidth);

      if (!showMoreMenu) return;

      const allTabs = React.Children.toArray(children);
      const { visibleTabsCount } = totalTabsWidth(
        allTabs,
        parentWidth,
        MORE_MENU_BUTTON_WIDTH_BOUND
      );

      const newTabs = allTabs.slice(0, visibleTabsCount);
      const newOverflowTabs = allTabs.slice(visibleTabsCount);
      if (visibleTabsCount === tabs.length && !isFitted) return;

      if (tabIndicatorRef.current) {
        if (selectedIndex < visibleTabsCount) {
          const selectedTab = tabListRef.current?.children[selectedIndex];
          if (selectedTab) {
            resetSelectedMenuItemRef();
            tabIndicatorRef.current.style.opacity = '1';
          }
        } else {
          const overflowedActiveTab = tabListRef.current?.children[
            selectedIndex
          ] as HTMLElement;
          selectedMenuItemRef.current = overflowedActiveTab;

          tabIndicatorRef.current.style.opacity = '0';
        }
      }

      setTabs(newTabs);
      setOverflowTabs(newOverflowTabs);
    }, [children, isFitted, selectedIndex, showMoreMenu, tabs.length]);

    const resetSelectedMenuItemRef = () => {
      selectedMenuItemRef.current = null;
    };

    const updatedTabs = tabs.map((child, index) => {
      const onClick = () => {
        if (tabIndicatorRef.current) {
          tabIndicatorRef.current.style.opacity = '1';
        }
        resetSelectedMenuItemRef();
        setSelectedIndex?.(index);
      };
      return React.cloneElement(child as React.ReactElement, {
        index: index,
        key: index,
        onClick,
      });
    });

    const updatedOverflowTabs = overflowTabs.map((child, index) => {
      const originalIndex: number = tabs.length + index;
      const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSelectedIndex?.(originalIndex);
        if (tabIndicatorRef.current) {
          tabIndicatorRef.current.style.opacity = '0';
        }
        selectedMenuItemRef.current = e.currentTarget;
      };

      return {
        tab: React.cloneElement(child as React.ReactElement, {
          index: originalIndex,
          key: originalIndex,
        }),
        onClick,
      };
    });

    useTabResizeObserver(tabListRef, recalculateTabs);

    return (
      <Box
        sx={getTabsContainerStyles(showMoreMenu || false)}
        data-testid="tabs-container"
      >
        <ChakraTabList
          ref={useMergeRefs(tabListRef, ref)}
          sx={getTabListBorderStyles(
            colorMode || 'light',
            orientation,
            showMoreMenu || false
          )}
          {...props}
        >
          {showMoreMenu && orientation !== 'vertical' ? (
            <>
              {updatedTabs}
              {updatedOverflowTabs.length > 0 && (
                <TabsShowMoreMenu
                  colorMode={colorMode}
                  tabs={updatedOverflowTabs}
                  selectedMenuItemRef={selectedMenuItemRef}
                />
              )}
            </>
          ) : (
            updatedTabs
          )}
        </ChakraTabList>
        <TabIndicator
          data-testid="tab-indicator"
          key={
            tabsListWidth && isFitted ? `fitted ${tabsListWidth}` : 'default'
          }
          ref={tabIndicatorRef}
          top="35px"
        />
      </Box>
    );
  }
);

export { TabPanels, TabPanel } from '@chakra-ui/react';
